import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAddCaseFormContext } from '../contexts/AddCaseFormContext';
import useDedicatedFileUpload from './useDedicatedFileUpload';
import useCaseFieldUpdate from './useCaseFieldUpdate';
import useCaseRecordDelete from './useCaseRecordDelete';
import { buildLiabilityPaths } from '../utils/caseFieldPaths';
import { generateRandomIds, getDefendantId, prepFileMetaDataForDedicatedUpload, filterDuplicateFilesByName } from '../utils/helper';
import { RECORD_DATA_TYPE, UPDATE_CASE_ACTION } from '@demandproai/shared';
import { updateCaseDataFile } from '../redux/actions/fileUpload';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { fetchDropboxFilesAsLocal } from '../utils/dropboxChooser';
import { validateFilesForUpload } from '../utils/fileValidation';

// Custom hook for liability file upload logic
const useLiabilityFileUpload = ({ fileType, accept = 'application/pdf' }) => {
  const { caseId } = useParams();
  const dispatch = useDispatch();
  const { formik, selectedDefendant } = useAddCaseFormContext();
  const caseDataFiles = useSelector(state => state.fileUpload.caseDataFiles);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [fileErrorList, setFileErrorList] = useState([]);

  const { isLoading: isFileLoading, dedicatedFileUpload, completeFileUpload, startFileUpload } =
    useDedicatedFileUpload(caseId, RECORD_DATA_TYPE.LIABILITY);
  const { updateCase } = useCaseFieldUpdate({ caseId });
  const { handleFileRemove } = useCaseRecordDelete();
  const libilityPaths = buildLiabilityPaths();

  const defendants = formik.values.defendants || [];
  const isSingleLiability = formik.values.caseInfo?.isSingleLiabilitySectionForAllDefendants;
  const isShowTooltip = defendants.length > 1 && !isSingleLiability;

  const files = formik.values.liability?.[fileType] || [];
  const fileArray = Array.isArray(files) ? files : files ? [files] : [];

  const isLoading = isFileLoading || isUploadingFiles;

  // Get applicable defendants based on single/multi liability mode
  const getApplicableDefendants = useCallback(() => {
    const isSingleLiability = formik.values.caseInfo?.isSingleLiabilitySectionForAllDefendants;
    const allDefIds = (formik.values.defendants || []).map(d => d.defendantId);

    return isSingleLiability ? allDefIds : [getDefendantId(formik.values.defendants[selectedDefendant])]
  }, [formik.values.defendants, formik.values.caseInfo?.isSingleLiabilitySectionForAllDefendants, selectedDefendant]);

  // Upload liability files for a specific field
  const handleLiabilityFileUpload = useCallback(async ({ files }) => {
    if (!files?.file?.length) return;
    const prevFiles = formik.values?.liability?.[fileType] || [];

    const uniqueFiles = filterDuplicateFilesByName({
      newFiles: files.file,
      prevFiles,
    });

    if (!uniqueFiles.length) return;

    const applicableDefendants = getApplicableDefendants();

    const fileArr = uniqueFiles.map(elem => ({
      id: generateRandomIds(),
      file: elem,
      defendants: applicableDefendants
    }));

    // Update formik immediately with temp files
    const tmpPath = `liability.${fileType}`;
    formik.setFieldValue(tmpPath, [...prevFiles, ...fileArr]);

    // Prepare and upload files
    const prepFiles = prepFileMetaDataForDedicatedUpload(fileArr);
    const fileCount = prepFiles.length;

    setIsUploadingFiles(true);
    startFileUpload(fileCount);

    try {
      let uploadedFiles = await dedicatedFileUpload(prepFiles, {
        fieldKey: libilityPaths.files(fileType),
        action: 'add',
      });

      uploadedFiles = uploadedFiles.map(file => ({
        ...file,
        defendants: file.metaData?.defendants || applicableDefendants
      }));

      // Update formik with uploaded files
      formik.setFieldValue(tmpPath, [...prevFiles, ...uploadedFiles]);
      completeFileUpload(fileCount);
    } catch (error) {
      console.error('Error uploading liability files:', error);
      completeFileUpload(fileCount);
      // Revert to previous files on error
      formik.setFieldValue(tmpPath, prevFiles);
    } finally {
      setIsUploadingFiles(false);
    }
  }, [formik, fileType, getApplicableDefendants, dedicatedFileUpload, startFileUpload, completeFileUpload, libilityPaths]);

  // Remove liability file
  const handleLiabilityFileRemove = useCallback(async ({ fileToRemove }) => {
    handleFileRemove({
      formik,
      fileToRemove,
      filePath: `liability.${fileType}`,
      removeData: { [libilityPaths.files(fileType)]: { s3UrlPath: fileToRemove.s3UrlPath } }
    })
  }, [formik, fileType, handleFileRemove, libilityPaths]);

  const handleDropboxSuccess = useCallback(async (files) => {
    try {
      const downloadedFiles = await fetchDropboxFilesAsLocal(files);
      const { validFiles, errorList } = await validateFilesForUpload({
        files: downloadedFiles,
        accept
      });
      if (errorList.length) {
        setFileErrorList(errorList);
      }
      if (validFiles.length > 0) {
        await handleLiabilityFileUpload({ files: { file: validFiles } });
      }
    } catch (error) {
      console.error('Error importing Dropbox files:', error);
    }
  }, [accept, handleLiabilityFileUpload]);

  // Update defendants for a specific liability file
  const updateLiabilityDefendants = useCallback(async ({ s3UrlPath, defendantId, isChecked }) => {
    const isSingleLiability = formik.values.caseInfo?.isSingleLiabilitySectionForAllDefendants;

    if (!isSingleLiability) {
      const files = formik.values?.liability?.[fileType] || [];

      files.forEach((file, index) => {
        if (file.s3UrlPath === s3UrlPath) {
          if (isChecked) {
            file.defendants.push(defendantId);
          } else {
            file.defendants = file.defendants.filter(def => def !== defendantId);
          }
          const caseDataFile = caseDataFiles.get(s3UrlPath);
          if(caseDataFile){
              dispatch(updateCaseDataFile({s3UrlPath, metaData:{...caseDataFile.metaData,defendants:file.defendants}}));
          }else{
            updateCase({
              data: { [libilityPaths.fileDefendants(fileType, index)]: file.defendants },
              action: UPDATE_CASE_ACTION.UPDATE
            });
          }
        }
      });

      const filePath = `liability.${fileType}`;
      formik.setFieldValue(filePath, files);
    }
  }, [formik, fileType, updateCase, libilityPaths]);

  return {
    fileArray,
    defendants,
    isSingleLiability,
    isShowTooltip,
    isLoading,
    fileErrorList,
    setFileErrorList,
    handleLiabilityFileUpload,
    handleDropboxSuccess,
    handleLiabilityFileRemove,
    updateLiabilityDefendants
  };
};

export default useLiabilityFileUpload;
